package ai.wagemix.web.screens.payment

import ai.wagemix.logic.WMRoutes
import ai.wagemix.logic.payment.WMDefaultPaymentResultPresenter
import ai.wagemix.logic.payment.WMPaymentResultPresenter
import ai.wagemix.logic.payment.WMPaymentResultPresenter.Action
import ai.wagemix.logic.payment.WMPaymentResultPresenter.Event
import ai.wagemix.net.models.WMCheckoutSummary
import ai.wagemix.net.models.firebase.WMSubscriptionType
import ai.wagemix.web.components.WMButton
import ai.wagemix.web.components.WMButtonKind
import ai.wagemix.web.components.WMEmptyState
import ai.wagemix.web.components.WMIconGlyph
import ai.wagemix.web.components.WMKeyValue
import ai.wagemix.web.components.WMPanel
import ai.wagemix.web.screens.WMWebStrings
import ai.wagemix.web.theme.WMLeadTextStyle
import androidx.compose.runtime.Composable
import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import com.fsryan.foundation.web.screens.FSWebScreen
import com.fsryan.foundation.web.screens.FSWebViewModel
import com.fsryan.foundation.web.screens.FSWebViewStateHolder
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.web.css.px

interface WMPaymentResultWebViewStateHolder: FSWebViewStateHolder {
    val summaryState: State<WMCheckoutSummary?>
    val failedState: State<Boolean>
}

abstract class WMBasePaymentResultWebViewModel<E: Event, A: Action, PR: WMPaymentResultPresenter<E, A>>(presenter: PR):
    FSWebViewModel<E, A, PR>(presenter), WMPaymentResultWebViewStateHolder {

    override val summaryState: State<WMCheckoutSummary?> get() = _summaryState
    protected val _summaryState = mutableStateOf<WMCheckoutSummary?>(null)

    override val failedState: State<Boolean> get() = _failedState
    protected val _failedState = mutableStateOf(false)

    override fun customEventHandling(event: E) {
        super.customEventHandling(event)
        with(event) {
            summaryUpdate?.let { _summaryState.value = it }
            failedUpdate?.let { _failedState.value = it }
        }
    }

    fun userRequestedRetry() = presenter.userRequestedRetry()
}

typealias WMDefaultPaymentResultWebViewModel = WMBasePaymentResultWebViewModel<Event.Base, Action.Base, WMDefaultPaymentResultPresenter>

fun createDefaultPaymentResultWebViewModel(presenter: WMDefaultPaymentResultPresenter): WMDefaultPaymentResultWebViewModel =
    object: WMDefaultPaymentResultWebViewModel(presenter) {}

@Composable
fun <E: Event, A: Action, PR: WMPaymentResultPresenter<E, A>, VM: WMBasePaymentResultWebViewModel<E, A, PR>> WMPaymentResultScreen(
    createViewModel: () -> VM,
    sessionId: String?,
    strings: WMWebStrings,
    modifier: Modifier = Modifier
) {
    FSWebScreen(createViewModel = createViewModel, key = sessionId) { vm ->
        WMPaymentResultScreenLayout(strings, vm.loadingState, vm.summaryState, vm.failedState, modifier, onRetry = vm::userRequestedRetry)
    }
}

@Composable
fun WMPaymentResultScreenLayout(
    strings: WMWebStrings,
    loadingState: State<Boolean>,
    summaryState: State<WMCheckoutSummary?>,
    failedState: State<Boolean>,
    modifier: Modifier = Modifier,
    onRetry: () -> Unit
) {
    val p = strings.payment
    val colors = ColorMode.current.toPalette().fs
    Column(modifier = Modifier.fillMaxWidth().maxWidth(560.px).gap(16.px).then(modifier)) {
        when {
            failedState.value -> WMEmptyState(WMIconGlyph.TriangleAlert, p.failedTitle, p.failedMessage) {
                WMButton(strings.common.retry, kind = WMButtonKind.Secondary, onClick = onRetry)
            }
            else -> WMPanel(title = p.title) {
                SpanText(p.thanks, WMLeadTextStyle.toModifier().color(colors.onSurfaceVariant))
                summaryState.value?.let { s ->
                    WMKeyValue(p.plan, when (s.plan) { WMSubscriptionType.Basic -> strings.common.basic; WMSubscriptionType.Pro -> strings.common.pro; WMSubscriptionType.None -> strings.common.free })
                    WMKeyValue(p.billingCycle, s.interval)
                    s.currentPeriodEnd?.let { WMKeyValue(p.nextPayment, it.toLocalDateTime(TimeZone.currentSystemDefault()).date.toString()) }
                } ?: SpanText(strings.common.loading, WMLeadTextStyle.toModifier().color(colors.onSurfaceVariant))
                WMButton(p.goToMix, leadingIcon = WMIconGlyph.ArrowRight) { kotlinx.browser.window.location.href = WMRoutes.MIX }
            }
        }
    }
}
